<template>
	<VDataGridV2
		ref="grid"
		entity-type="store"
		sticky
		paginated

		:loader="loader"
		:skeleton-row-count="40"
		:context-menu="StoresService.contextMenu"

		@row:dblclick="openStoreCard"
	>
		<VDataGridColumnV2
			id="id"
			width="75px"
			content-align="left"
			label="№"
		>
			<template #default="{ row }">
				<VButton
					theme="link"
					@click="openStoreCard(row)"
				>
					{{ row.id }}
				</VButton>
        <div class="description">
          <VBadge
            v-if="row.isDraft"
            class="draft"
          >
            Черновик
          </VBadge>
        </div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="partner"
			width="250px"
			label="Партнер"
			filterable
			filter-send-name="pattern"
			:filter-props="{
      	placeholder: 'Введите название',
				clearable:   true,
			}"
			content-class="nowrap ellipsis"
		>
			<template #default="{ row }">
				<span :title="row.partner?.name">
					{{ row.partner?.name }}
				</span>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="name"
			width="400px"
			label="Склад"
			content-class="nowrap ellipsis"
		>
			<template #default="{ row }">
				<span
          :title="row.name || row.address"
        >
					{{ row.name || row.address }}
				</span>
				<div
          class="description"
        >
          <div
            v-if="row.name"
            :title="row.address"
            class="nowrap ellipsis"
          >
            {{ row.address }}
          </div>
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="transportZone"
			width="150px"
			label="Транспортная зона"
			sortable
			sort-default-value="asc"
			header-class="nowrap"
      content-class="ellipsis"
			@edit="(row, anchor) => openZonePopup(row, anchor)"
		>
			<template #default="{ row }">
				{{ row.transportZone ? row.transportZone.name : '--' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="hasTransportZone"
			width="120px"
			label="Есть зона"
			filterable
			filter-type="select"
			filter-identity="hasTransportZone"
			header-class="nowrap"
			:filter-props="{
      	options:     StoresService.HAS_TRANSPORT_ZONE_OPTIONS,
				placeholder: 'Все',
				clearable:   true,
			}"
			:filter-default-value="StoresService.HAS_TRANSPORT_ZONE_OPTIONS[1]"
		>
			<template #default="{ row }">
				{{ row.transportZone ? 'Да' : 'Нет' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="isDraft"
			width="130px"
			label="Черновик"
			filterable
			filter-type="select"
			filter-identity="isDraft"
			:filter-props="{
      	options:     StoresService.IS_DRAFT_OPTIONS,
				placeholder: 'Все',
				clearable:   true,
			}"
			:filter-default-value="StoresService.IS_DRAFT_OPTIONS[0]"
		>
			<template #default="{ row }">
				{{ row.isDraft ? 'Да' : 'Нет' }}
			</template>
		</VDataGridColumnV2>
	</VDataGridV2>

	<!-- Кастомный локальный popup для быстрого назначения транспортной зоны по клику в ячейке зоны.
			 Никакой существующий popup для этих целей не подходит — быстрее сделать такой. -->
	<div
		v-if="transportZonePopup.visible"
		ref="change-transport-zone"
		class="mds-popup reason-refuse-popup"
		:class="{'visible': transportZonePopup.visible}"
		:style="transportZonePopup.dialogStyle"
	>
		<VSelect
			v-if="transportZonePopup.visible"
			ref="transportZoneControl"
			layout="vertical"
			data-url="transportZones.getList"
			label="Транспортная зона"
			placeholder="Введите транспортную зону"
			v-model="transportZonePopup.store.transportZone"
			@update:modelValue="onTransportZoneInput"
		/>
	</div>
</template>

<script lang="ts">

import type Store      from '@/domains/Stores/Store';
import StoresService     from '@/domains/Stores/StoresService';
import DataGridLoader    from '@/lib/components/VDataGridV2/DataGridLoader';

export default defineComponent({
	name: 'StoresRegistry',

	setup() {
		return {
			StoresService,
		};
	},

	data() {
		return {
			loader: new DataGridLoader(StoresService.getListPaginated.bind(StoresService)),

			transportZonePopup: {
				visible:     false,
				store:       null as Store | null,
				/** Ячейка, по которой открыли попап: от неё считается позиция, клик по ней не считается кликом снаружи */
				anchor:      null as HTMLElement | null,
				dialogStyle: null as Record<string, any> | null,
			},
		};
	},

	created() {
		window.addEventListener('click', this.onClickOutsideCustom);
	},

	beforeUnmount() {
		window.removeEventListener('click', this.onClickOutsideCustom);
	},

	methods: {
		async openStoreCard(row: Store): Promise<void> {
			if (await StoresService.editStore(row) === true) {
				this.onListChanged();
			}
		},

		/** Любое изменение в списке (создан/обновлён/удалён склад) — перезагрузить грид */
		onListChanged() {
			this.loader.refresh();
		},

		async onTransportZoneInput(value: any) {
			const updatedStore = {
				...this.transportZonePopup.store,
				transportZone: value,
			} as Store;

			await StoresService.update(updatedStore);

			this.closeTransportZonePopup();
			this.onListChanged();
		},

		async openZonePopup(row: Store, anchor: HTMLElement) {
			/** Повторный клик по той же строке — закрываем попап */
			if (row.id === this.transportZonePopup.store?.id) {
				this.closeTransportZonePopup();
			}
			else {
				this.closeTransportZonePopup();

				await this.$nextTick();
				this.showTransportZonePopup(anchor, row);
			}
		},

		async showTransportZonePopup(anchor: HTMLElement, entity: Store) {
			const rect = anchor.getBoundingClientRect();

			this.transportZonePopup.store  = entity;
			this.transportZonePopup.anchor = anchor;

			/** Попап встаёт под ячейкой */
			this.transportZonePopup.dialogStyle = {
				zIndex:  70,
				padding: '10px',
				left:    `${rect.left}px`,
				top:     `${rect.bottom + 5}px`,
				display: 'block',
			};

			this.transportZonePopup.visible = true;

			await this.$nextTick();
			(this.$refs.transportZoneControl as any).doFocus();
		},

		closeTransportZonePopup() {
			this.transportZonePopup.store   = null;
			this.transportZonePopup.anchor  = null;
			this.transportZonePopup.visible = false;
		},

		onClickOutsideCustom(event: MouseEvent) {
			const popupEl = this.$refs['change-transport-zone'] as HTMLElement | undefined;
			const target  = event.target as Node;

			if (this.transportZonePopup.visible === false) {
				// попап закрыт — нечего проверять
			}
			else if (this.transportZonePopup.anchor?.contains(target) === true) {
				// клик по ячейке, которой попап открыли, — его обрабатывает openZonePopup
			}
			else if (this.$core.utils.isNullOrUndefined(popupEl) === false && popupEl.contains(target) === false) {
				this.closeTransportZonePopup();
			}
			else {
				// клик внутри попапа
			}
		},
	},
});
</script>
